import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import {
    ReactiveFormsModule,
    FormControl,
    FormGroup,
    FormsModule,
    Validators
} from '@angular/forms';
import { Router } from '@angular/router';
import { HttpStatusCode } from '@angular/common/http';
import { ButtonModule } from 'primeng/button';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { AuthAPIService } from '@services/AuthService/auth.api.service';
import { AppSettingsService } from '@core/services/app-settings/app-settings.service';
import { CustomValidators } from '@shared/validation/validators/custom-validators';
import { passwordErrorMessages } from '@shared/validation/password-policy';
import { AlertService } from '@core/services/alert/alert.service';
import { UserInfo } from '@models/User';
import { FormUtilitiesService } from '@shared/services/FormUtilitiesService/form-utilities.service';
import { FormValidationService } from '@shared/services/form-validation/form-validation.service';
import { FIELD_ERROR_MESSAGES } from '@constants/field-error-messages.constants';
import { FieldErrorComponent } from '@shared/component/general/field-error/field-error.component';
import { AlphaNumericDirective } from '@shared/directives/alphaNumeric.directive';
import { OnlyUppercaseDirective } from '@shared/directives/uppercase.directive';
import { MessageModule } from 'primeng/message';

@Component({
    selector: 'app-login',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ButtonModule,
        InputTextModule,
        PasswordModule,
        FieldErrorComponent,
        ReactiveFormsModule,
        IconFieldModule,
        InputIconModule,
        MessageModule,
        AlphaNumericDirective,
        OnlyUppercaseDirective,
    ],
    templateUrl: 'login.component.html',
    styleUrl: 'login.component.scss'
})
export class LoginComponent {
    private static readonly GENERIC_ERROR_MESSAGES: Record<string, string> = {
        required: 'This field is required.',
        minlength: 'Input is too short.',
        maxlength: 'Input is too long.'
    };

    private appSettings = inject(AppSettingsService);

    isLoading: boolean = false;
    isError: boolean =false;
    userInfo!: UserInfo;
    errorMsg = "";

    isShowAuthorizationPendingDialog: boolean = false;
    authorizationPendingMessage: string = '';

    loginForm = new FormGroup({
        username: new FormControl('', [Validators.required, Validators.minLength(3)]),
        // Length only — character rules never apply at login, since existing
        // passwords may predate the current policy.
        password: new FormControl('', [
            Validators.required,
            CustomValidators.passwordLength(() => this.appSettings.passwordRequirement)
        ])
    });

    constructor(
        private router: Router,
        private authAPIService: AuthAPIService,
        private alertService: AlertService,
        private formUtilitiesService: FormUtilitiesService,
        private formValidationService: FormValidationService
    ) {}

    onSubmit() {
        if (this.formValidationService.rejectIfInvalid(this.loginForm)) {

            const messages = this.getAllFormErrorMessages();

            if (messages.length > 0) {
                const formatted = messages.join('\n');
                this.alertService.showError('Invalid Form', formatted);
            } else {
                this.alertService.showError('Invalid Form', 'Please fill in all required fields.');
            }

            return;
        }

        const credentials = {
            username: this.username.value,
            password: this.password.value
        };

        this.disableAllFormFields();
        this.callCredential(credentials);
    }

    private getAllFormErrorMessages(): string[] {
        const messages: string[] = [];

        Object.keys(this.loginForm.controls).forEach((controlName) => {
            const control = this.loginForm.get(controlName);
            if (control?.errors) {
                const fieldMessages =
                    controlName === 'password'
                        ? { ...FIELD_ERROR_MESSAGES[controlName], ...this.passwordMessages }
                        : (FIELD_ERROR_MESSAGES[controlName] ?? {});
                const errorKey = Object.keys(control.errors).find((k) => k !== 'server');
                if (errorKey) {
                    // Fall back per validator key so a minlength failure is not reported as "required".
                    const message =
                        fieldMessages[errorKey] ??
                        LoginComponent.GENERIC_ERROR_MESSAGES[errorKey] ??
                        'Please check this value.';
                    messages.push(message);
                }
            }
        });

        return messages;
    }

    private callCredential(credentials: { username: string; password: string }) {
        this.authAPIService.login(credentials).subscribe({
            next: (response) => {
                this.enableAllFormFields();
                const httpStatusCode = response.status;

                if (httpStatusCode === HttpStatusCode.Accepted) {
                    this.isShowAuthorizationPendingDialog = true;
                    this.authorizationPendingMessage =
                        response.body?.message ||
                        'Your account is pending authorization. Please wait for approval.';
                    return;
                }

                this.router.navigate(['/dashboard']);
                this.alertService.showSuccess('Authorized', response.body?.message);
            },
            error: (err) => {

                this.isError =  true;
                this.errorMsg = err.message;
                this.enableAllFormFields();
                if (err.statusCode === HttpStatusCode.UnprocessableEntity) {
                    this.formUtilitiesService.applyServerFieldErrors(

                        this.loginForm,
                        err.error.fieldError
                    );
                }
                return;
            }
        });
    }

    enableAllFormFields() {
        this.isLoading = false;
        this.username.enable();
        this.password.enable();
    }

    disableAllFormFields() {
        this.isLoading = true;
        this.username.disable();
        this.password.disable();
    }

    // #region --- Getter
    get username(): FormControl {
        return this.loginForm.get('username') as FormControl;
    }

    get password(): FormControl {
        return this.loginForm.get('password') as FormControl;
    }

    get minPasswordLength(): number {
        return this.appSettings.passwordRequirement.passwordMinLength;
    }

    get maxPasswordLength(): number {
        return this.appSettings.passwordRequirement.passwordMaxLength;
    }

    get passwordMessages(): Record<string, string> {
        return passwordErrorMessages(this.appSettings.passwordRequirement);
    }

    get logoUrl(): string {
        return (
            this.appSettings.appearance?.companyLogoUrl || 'assets/images/logos/default_logo.png'
        );
    }
    // #endregion
}
